import type { OverviewPeriod, RundownPeriod } from '@brush/ui';

/**
 * The period choices for this tab (docs/specs/user-preferences.md UP25, part 1a): held in memory for the session
 * only — no browser storage of either kind, no server write — so a reload or a new tab starts from the defaults.
 * Keyed by the choice alone and never by a room, and mounted above the room provider (main.tsx), so a choice made in
 * one room applies in the next and in every section.
 *
 * The keys are part 1b's registry keys, and the shape of a choice is what 1b stores, so 1b can put the server behind
 * this same interface (§7.1) without the pickers changing.
 */
export const PERIOD_KEYS = ['rundown.period', 'overview.totals'] as const;

export type PeriodKey = (typeof PERIOD_KEYS)[number];

export interface PeriodChoices {
  'rundown.period': RundownPeriod;
  'overview.totals': OverviewPeriod;
}

export interface PeriodStore {
  get<K extends PeriodKey>(key: K): PeriodChoices[K];
  set<K extends PeriodKey>(key: K, value: PeriodChoices[K]): void;
  subscribe(listener: () => void): () => void;
}

/** A store starting from <code>initial</code> — the provider passes the shared defaults. */
export function createPeriodStore(initial: PeriodChoices): PeriodStore {
  let state: PeriodChoices = { ...initial };
  const listeners = new Set<() => void>();
  return {
    get: (key) => state[key],
    set: (key, value) => {
      if (state[key] === value) return;
      state = { ...state, [key]: value };
      for (const listener of listeners) listener();
    },
    subscribe: (listener) => {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
  };
}
